import { useEffect, useState, useRef } from "react";
import "./LoginComponent.css";
import NextBtn from "../../../../Shared/Components/NextBtn/NextBtn";
import { useNavigate } from "react-router-dom";
import { verifyOtp, resendOtp } from "../../../../Actions/UserActions";
import { PhoneAuthProvider, signInWithCredential } from "firebase/auth";

import {
  getMobileNumber,
  saveConsentFlag,
  saveOnboardingFlag,
  setUserProfileData,
  setUserToken,
  stopPlayingAudio,
  setUserType,
  setAgents,
  setUserKb,
  setUserCadre,
  setUserApproach,
  setUserId,
  setTooltip,
  getTooltip,
  handleTooltipOnboarding,
  CheckLanguageSupport,
  playAudio,
} from "../../../../Actions/Utils";
import { auth } from "../../../../firebaseConfig";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import CountdownTimer from "../../../../Shared/Components/CountdownTimer/CountdownTimer";
import ToastMessage from "../../../../Shared/Components/ToastMessage/ToastMessage";
import { ToastSeverityMap } from "../../../../NewConstants/ToastConstants";
import platform from "platform";
import {trackEvent} from "../../../../Actions/Utils";
import { getOtpSentLabel, loginConstant, resendOtpConstants, resendOtpTimer } from "../../../../NewConstants/LoginConstant";
import { guideButtons } from "../../../../NewConstants/GuideConstant";
import { isIframeDeployment } from "../../../../Actions/Utils";

const LoginComponent = ({ selectedLanguage, audioRef, setStepIndex, setStartTour, onBack }) => {
  const navigate = useNavigate();
  // OTP state as array for 6 digits
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const [showErrorFlag, setShowErrorFlag] = useState(false);
  const [showSpeakerActive, setShowSpeakerActive] = useState(false);
  const [requestController] = useState(null);
  const mobileNumber = getMobileNumber();
  const timerLength = 1;
  const [resendOtpFlag, setResendOtpFlag] = useState(false);
  const [showTimerFlag, setShowTimerFlag] = useState(true);
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [toastErrorMessage, setToastErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [captchaSolved, setCaptchaSolved] = useState(false);

  // ---- OTP HANDLERS ----
  const handleOtpChange = (e, idx) => {
    setShowErrorFlag(null);
    const digitsOnly = (e.target.value ?? "").replace(/\D/g, "");
    if (digitsOnly === "") {
      const newOtp = [...otp];
      newOtp[idx] = "";
      setOtp(newOtp);
      return;
    }
    // maxLength=1 usually prevents multi-char input, but paste/IME/autofill can inject more.
    // If multiple digits arrive, spread them across subsequent OTP boxes.
    if (digitsOnly.length > 1) {
      const digits = digitsOnly.split("").slice(0, 6 - idx);
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        newOtp[idx + i] = d;
      });
      setOtp(newOtp);
      const nextIdx = idx + digits.length;
      inputRefs.current[Math.min(nextIdx, 5)]?.focus();
      return;
    }

    const val = digitsOnly;
    const newOtp = [...otp];
    newOtp[idx] = val;
    setOtp(newOtp);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e, idx) => {
    if (!captchaSolved) return;
    setShowErrorFlag(null);
    const text = e.clipboardData?.getData("text") ?? "";
    const digitsOnly = text.replace(/\D/g, "");
    if (!digitsOnly) return;

    // Prevent the browser from trying to paste into a single maxLength=1 input.
    e.preventDefault();

    const digits = digitsOnly.split("").slice(0, 6 - idx);
    const newOtp = [...otp];
    digits.forEach((d, i) => {
      newOtp[idx + i] = d;
    });
    setOtp(newOtp);

    const nextIdx = idx + digits.length;
    inputRefs.current[Math.min(nextIdx, 5)]?.focus();
  };

  const handleOtpKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      if (otp[idx]) {
        const newOtp = [...otp];
        newOtp[idx] = "";
        setOtp(newOtp);
      } else if (idx > 0) {
        inputRefs.current[idx - 1]?.focus();
      }
    }
  };
  // Get or create a singleton RecaptchaVerifier bound to the current DOM node
  const generateRecaptcha = async () => {
    try {
      // If an instance already exists, reset and reuse it
      if (window.recaptchaVerifier) {
        setCaptchaSolved(false);
        try {
          // Ensure it's rendered and then reset the widget
          await window.recaptchaVerifier.render();
          window.recaptchaVerifier.reset();
        } catch (e) {
          // If render throws due to already rendered, a reset is still attempted
          try { window.recaptchaVerifier.reset(); } catch (_) {}
        }
        return window.recaptchaVerifier;
      }

      // Create a new verifier if one doesn't exist
      const container = document.getElementById('recaptcha-container');
      if (!container) {
        throw new Error('reCAPTCHA container not found');
      }
      container.innerHTML = '';

      setCaptchaSolved(false);
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          setCaptchaSolved(true);
        },
        'expired-callback': () => {
          setCaptchaSolved(false);
        }
      });
      await verifier.render();
      window.recaptchaVerifier = verifier;
      return verifier;
    } catch (error) {
      console.error("Error generating recaptcha:", error);
      setToastErrorMessage('Error setting up verification. Please try again.');
      setShowToastMessage(true);
      throw error;
    }
  };

  useEffect(() => {
    stopPlayingAudio(audioRef);
    window.history.pushState(null, document.title, window.location.href);

    const handlePopState = (event) => {
      window.history.pushState(null, document.title, window.location.href);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [audioRef]);

  // Send OTP on initial mount with a fresh Recaptcha verifier
  const sendInitialOtp = async (mobileNumber) => { // Pass mobileNumber as argument
    try {
      const appVerifier = await generateRecaptcha();
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        "+91" + mobileNumber,
        appVerifier
      );
      sessionStorage.setItem(
        "verificationId",
        confirmationResult.verificationId
      );
      // In case callback timing differs, ensure OTP input becomes enabled
      // Only set captchaSolved to true if the reCAPTCHA callback hasn't already done so.
      if (!captchaSolved) {
        setCaptchaSolved(true);
      }
    } catch (error) {
      console.error("Error sending initial OTP:", error);
      setToastErrorMessage("Error sending OTP. Please try again.");
      setShowToastMessage(true);
    }
  };

  useEffect(() => {
    if (mobileNumber) {
      sendInitialOtp(mobileNumber); // Call with mobileNumber
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileNumber]); // Depend on mobileNumber

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) { /* noop */ }
        window.recaptchaVerifier = null;
      }
    };
  }, []);

  
  const goToNext = async (data) => {
    setAgents(data?.agents);
    setTooltip(data?.tooltip_data);
    if (data?.agents.length <= 0){
      navigate("/login");
    }else{
      setShowSpeakerActive(false);
      stopPlayingAudio(audioRef);
      if (data?.type === "Internal" && data?.agents.length > 1){
        navigate("/kb-selection");
      }else{
        const agent = data.agents[0];
        setUserCadre(agent?.cadre);
        setUserKb(agent?.kb);
        setUserApproach(agent?.approach);
        saveOnboardingFlag(agent?.onboarding);
        saveConsentFlag(agent?.consent);
        if (!agent?.onboarding){
          navigate("/guide");
        }else if (!agent?.consent && !isIframeDeployment()){
          navigate("/consent");
        }else{
          await navigateBasedOnSupport();
        }
      }
    }
  };

  const optTimeoutOccured = () => {
    setShowTimerFlag(false);
    setResendOtpFlag(true);
  };

  const verifyOtpFun = async () => {
    setIsLoading(true);
    try {
      // ✅ Get verificationId from sessionStorage
      const storedVerificationId = sessionStorage.getItem("verificationId");
      if (!storedVerificationId) {
        setToastErrorMessage("Session expired. Please try again.");
        setShowToastMessage(true);
        navigate("/login");
        return;
      }
      // ✅ Generate Firebase credentials using verificationId + OTP
      const credential = PhoneAuthProvider.credential(storedVerificationId, otp.join(""));
      // ✅ Sign in with the credential
      const firebaseCredential = await signInWithCredential(auth, credential);
      if (firebaseCredential.user) {
        const deviceData = {
          device_name: platform.manufacturer || "Unknown",
          device_os: platform.os?.family || "Unknown",
          device_version: platform.os?.version || "Unknown",
        };
        const browserData = {
          browser_name: platform.name || "Unknown",
          browser_version: platform.version || "Unknown",
        };

        const res = await verifyOtp(
          mobileNumber,
          firebaseCredential._tokenResponse.idToken,
          deviceData,
          browserData,
          selectedLanguage
        );
  
        if (res?.status === 200) {
          if (res?.data?.token) {
            setUserToken(res?.data?.token);
            res["data"]["user_data"]["mobile"] = mobileNumber;
            setUserType(res?.data?.user_data?.type);
            setUserProfileData(res?.data?.user_data?.profile);
            setUserId(res?.data?.user_data?.user_id);

            // Show success toast message
            setToastErrorMessage("OTP verification successful!");
            setShowToastMessage(true);

            goToNext(res?.data?.user_data);

            // Track successful login
            trackEvent("Login Process", "Success", "Login Successfully");
          }
        } else if (res?.status === 401) {
          setShowErrorFlag(true);
          trackEvent("Login Process", "Failed", "Invalid OTP");
        } else if (res?.status === 500) {
          setToastErrorMessage(res?.data?.detail);
          setShowToastMessage(true);
          trackEvent("Login Process", "Failed", "Server Error");
        } else if (res?.status === 400) {
          setToastErrorMessage(res?.data?.detail);
          setShowToastMessage(true);
          trackEvent("Login Process", "Failed", "Bad Request");
        }
      }
    } catch (error) {
      console.error("Firebase verification error:", error);
      setShowErrorFlag(true);
      setToastErrorMessage("Invalid OTP. Please try again.");
      setShowToastMessage(true);
      trackEvent("Login Process", "Failed", "Firebase Verification Failed");
    } finally {
      setIsLoading(false);
    }
  };

  const resendOtpFun = async () => {
    setIsLoading(true);
    setShowErrorFlag(false);
    setResendOtpFlag(false);
    setShowTimerFlag(true);
    setOtp(["", "", "", "", "", ""]);
    try {
      // Always create a fresh Recaptcha instance to avoid stale DOM references
      const appVerifier = await generateRecaptcha();
      const confirmationResult = await signInWithPhoneNumber(auth, "+91" + mobileNumber, appVerifier);
      sessionStorage.setItem('verificationId', confirmationResult.verificationId);

      // Call backend resend OTP API
      const res = await resendOtp(`+91${mobileNumber}`);
      if (res?.status === 200) {
        setResendOtpFlag(false);
        trackEvent("Login Process", "Resend OTP", "Success");
      } else if (res?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/login");
      } else if (res?.status === 500) {
        setToastErrorMessage(res?.data?.detail);
        setShowToastMessage(true);
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      setToastErrorMessage("Error sending OTP. Please try again.");
      setShowToastMessage(true);
      trackEvent("Login Process", "Resend OTP", "Failed");
      
      // Reset recaptcha on error
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showSpeakerActive) {
      const filePath = `/Assets/Sounds/OTPVerifyPage/${selectedLanguage}_otp.mp3`;
      playAudio(filePath, audioRef, () => setShowSpeakerActive(false));
    } else {
      stopPlayingAudio(audioRef);
    }
  }, [showSpeakerActive, selectedLanguage, audioRef]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      stopPlayingAudio(audioRef);
      setShowSpeakerActive(false);
    };
    if (!mobileNumber) {
      navigate("/login");
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [audioRef, mobileNumber, navigate]);

  const navigateBasedOnSupport = async () => {
    const route = await CheckLanguageSupport();
    if (route === "chat"){
      const tooltip = getTooltip();
      handleTooltipOnboarding(tooltip, setStepIndex, setStartTour);
      navigate("/chat");
    }else if (route === "language-check"){
      navigate("/language-check");
    }else{
      navigate("/login");
    }
  };
  const handleBack = () => {
    onBack();
    stopPlayingAudio(audioRef);
    setShowSpeakerActive(false);
  };

  return (
    <>
      <div
        className="h-screen w-screen flex flex-col relative bg-[var(--primary-dark-2)] bg-no-repeat bg-cover bg-center bg-blend-color-burn"
        style={{ backgroundImage: `url("/Assets/Images/wallpaperBg.jpg")` }}
      >
        <div className="flex w-[100%] flex-col items-center justify-center px-4 pt-2">
          <div className="flex items-center justify-end w-full">
            <div
              className={`speakerDiv ${showSpeakerActive ? "Active" : ""}`}
              onClick={() => {
                setShowSpeakerActive((prev) => !prev);
              }}
            >
              {showSpeakerActive ? guideButtons.stop[selectedLanguage] : null}
              {showSpeakerActive ? (
                <div className="">
                  <img
                    src={"/Assets/Images/blueSpeaker.svg"}
                    alt="active Speaker"
                  />
                </div>
              ) : (
                <div className="">
                  <img
                    src={"/Assets/Images/crossSpeakerIcon.svg"}
                    alt="speaker"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="loginInputComponent w-full mb-8">
          <div className="flex flex-col items-center justify-center">
            <img className="ml-[3rem]" src={"/Assets/Images/Lady_Illustrations/OTP.svg"} alt="otpheadinglabel" />
            <div className="w-full flex flex-col items-center justify-center w-[90%] pt-[2rem] pb-8">
              <div className="text-center text-[1.5rem] text-white font-[700] pb-2">
                {loginConstant.otpTitle[selectedLanguage]}
              </div>
              <div className="flex flex-col items-center">
              </div>
            </div>
          </div>
          <div className="w-full flex flex-col items-center justify-center gap-2">
            <div className="otp-container pl-1 flex flex-wrap items-center gap-1 text-[1rem] text-white mb-2">
              <span>{getOtpSentLabel.first[selectedLanguage]}</span>
              <span onClick={handleBack} className="inline-flex items-center whitespace-nowrap">
                <span className="font-bold underline">{mobileNumber}</span>
                <img src="/Assets/Images/pen.svg" alt="pen" />
                </span>
              <span>{getOtpSentLabel.second[selectedLanguage]}</span>
            </div>
            <div className="otp-container flex justify-between items-center mb-2" style={{opacity: !captchaSolved ? 0.5 : 1}}>
              {otp.map((digit, idx) => (
                <div
                  key={idx}
                  className={`flex flex-col items-center rounded-[10px] shadow-[0_2px_8px_rgba(44,62,80,0.07)] w-[3.5rem] h-[3.5rem] relative border-[1.5px] ${showErrorFlag ? 'border-[var(--red-1)] bg-[var(--red-3)]' : 'border-[#CAE1DA] bg-white'}`}
                >
                  <input
                    className="border-none outline-none bg-transparent w-full h-full text-center text-2xl font-medium tracking-widest my-auto z-10"
                    type="tel"
                    maxLength={1}
                    pattern="[0-9]{1}"
                    inputMode="numeric"
                    value={digit}
                    onChange={(e) => handleOtpChange(e, idx)}
                    onPaste={(e) => handleOtpPaste(e, idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    disabled={!captchaSolved}
                    autoFocus={idx === 0}
                  />
                  {/* Bar at the bottom */}
                  {!digit && (
                    <div className="w-10 h-1 rounded bg-[#A2A2A2] mt-auto mb-2 z-0 transition-colors duration-200" />
                  )}
                </div>
              ))}
            </div>
            {!captchaSolved && (
              <div className="otp-container text-sm text-[var(--dark-neutral-2)]">
                {loginConstant.verifyingCaptcha?.[selectedLanguage] || "Verifying you are human..."}
              </div>
            )}
            {showTimerFlag && (
              <div className="otp-container flex text-sm text-white gap-1 pl-1">
                <span>{resendOtpTimer.first[selectedLanguage]}</span>
                <CountdownTimer
                  timerLength={timerLength}
                  timeoutOccured={optTimeoutOccured}
                />
                <span>{resendOtpTimer.second[selectedLanguage]}</span>
              </div>
            )}
            {resendOtpFlag && (
              <div className="otp-container flex text-sm text-white gap-1 ">
                <span>{resendOtpConstants.first[selectedLanguage]}</span>
                <span onClick={resendOtpFun} className="font-bold underline">{resendOtpConstants.second[selectedLanguage]}</span>
              </div>
            )}
          </div>
        </div>
        <div className="loginNextBtn">
          {
            <NextBtn
              value={isLoading ? loginConstant.verifying[selectedLanguage] : loginConstant.proceedButton[selectedLanguage]}
              icon={"/Assets/Images/nextBlueIcon.svg"}
              bgColor={"var(--neutral-white)"}
              color={"var(--primary-dark)"}
              nextClick={verifyOtpFun}
              padx={48}
              disabled={isLoading || otp.join("").length !== 6}
              opacity={otp.join("").length !== 6 ? 0.5 : 1}
            />
          }

        </div>
        {/* Invisible reCAPTCHA container */}
        <div id="recaptcha-container"></div>
      </div>
      {showToastMessage && (
        <ToastMessage
          message={toastErrorMessage}
          severity={ToastSeverityMap.error}
          direction={"bottom"}
          close={setShowToastMessage}
        />
      )}
    </>
  );
};

export default LoginComponent;
